import React, { useCallback, useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { supabase } from '../lib/supabaseClient';
import { ROLES } from './constants';
import './UserManagement.css';

const EMPTY = { full_name: '', email: '', password: '', role: 'manager' };

export default function UserManagement() {
  const [people, setPeople] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { type: 'ok' | 'err', text }

  const load = useCallback(async () => {
    const { data } = await supabase
      .from('profiles')
      .select('id, full_name, email, role')
      .order('full_name');
    setPeople(data || []);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ---- Create a login (calls the secure Netlify function) ----------------
  const createUser = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      const res = await fetch('/.netlify/functions/admin-create-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });
      const out = await res.json();
      if (!res.ok) throw new Error(out.error || 'Could not create user.');
      setMsg({ type: 'ok', text: `Created login for ${form.email}` });
      setForm(EMPTY);
      load();
    } catch (err) {
      setMsg({ type: 'err', text: err.message });
    } finally {
      setBusy(false);
    }
  };

  // ---- Inline edit of an existing person ---------------------------------
  const updatePerson = async (id, patch) => {
    setPeople((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
    const { error } = await supabase.from('profiles').update(patch).eq('id', id);
    if (error) setMsg({ type: 'err', text: error.message });
  };

  return (
    <AdminLayout title="People">
      <div className="people-grid">
        {/* Create login */}
        <section className="people-panel">
          <h3>Create a login</h3>
          <form className="people-form" onSubmit={createUser}>
            <div className="field">
              <label>Full name</label>
              <input
                className="input"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                required
              />
            </div>
            <div className="field">
              <label>Email</label>
              <input
                className="input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>
            <div className="field">
              <label>Temporary password</label>
              <input
                className="input"
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                minLength={8}
                placeholder="At least 8 characters"
                required
              />
            </div>
            <div className="field">
              <label>Role</label>
              <select
                className="select"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              >
                {ROLES.map((r) => (
                  <option key={r.key} value={r.key}>
                    {r.label}
                  </option>
                ))}
              </select>
              <span className="field-help">{ROLES.find((r) => r.key === form.role)?.help}</span>
            </div>

            {msg && (
              <div className={msg.type === 'ok' ? 'people-ok' : 'people-err'}>{msg.text}</div>
            )}

            <button className="btn btn-primary" type="submit" disabled={busy}>
              {busy ? 'Creating…' : 'Create login'}
            </button>
          </form>
        </section>

        {/* Existing people */}
        <section className="people-panel">
          <h3>Team ({people.length})</h3>
          <div className="people-list">
            {people.map((p) => (
              <div key={p.id} className="person-row">
                <div className="person-id">
                  <div className="person-name">{p.full_name || '—'}</div>
                  <div className="person-email">{p.email}</div>
                </div>
                <select
                  className="mini-select"
                  value={p.role}
                  onChange={(e) => updatePerson(p.id, { role: e.target.value })}
                >
                  {ROLES.map((r) => (
                    <option key={r.key} value={r.key}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </section>
      </div>
    </AdminLayout>
  );
}
